'use client';
import {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import getWishlist from '@/api/getWhishlist';
import addWishlist from '@/api/addWishlist';
import removeWishlist from '@/api/removeWishlist';
import { WishlistContextType } from '@/types/context';
import useBackendUser from '@/hooks/useBackendUser';
import { WishlistV2 } from '@/types/api/api-v2';
import parseWithType from '@/utilites/parse-with-type';
import { SkuDTO } from '@/types/api/sku';

// Default Wishlist State
const defaultWishlist: WishlistV2 = {
  userId: '',
  createdAt: '',
  updatedAt: '',
  skus: [],
};

export const WishlistContext = createContext<WishlistContextType>({
  wishlist: defaultWishlist,
  addWishlist: () => {},
  removeWishlist: () => {},
  isLoadingWishlist: false,
  toggleFromWishlist: () => {},
  isInWishlist: () => false,
});

const WishlistContextProvider = ({ children }: { children: ReactNode }) => {
  const { data: user } = useBackendUser();

  // Memoized Initial State for Wishlist
  const getInitialWishlist = useCallback(() => {
    if (typeof window === 'undefined') return defaultWishlist;
    try {
      const data = window.localStorage.getItem('wishlist');
      const wishlist = parseWithType<WishlistV2>(data);
      if (
        wishlist &&
        'userId' in wishlist &&
        'createdAt' in wishlist &&
        'updatedAt' in wishlist &&
        'skus' in wishlist &&
        Array.isArray(wishlist.skus)
      ) {
        return wishlist;
      }
    } catch (error) {
      console.error('Error parsing wishlist from localStorage:', error);
      return defaultWishlist;
    }
    return defaultWishlist;
  }, []);

  const [wishlist, setWishlist] = useState<WishlistV2>(getInitialWishlist);

  // Query to Fetch Wishlist Data
  const wishlistQuery = useQuery({
    queryKey: ['wishlist', user?.id],
    queryFn: getWishlist,
    enabled: !!user?.id,
    retry: false,
  });

  // Mutations to Add/Remove Wishlist Items
  const addWishlistMutation = useMutation({
    mutationFn: addWishlist,
    onSuccess: () => wishlistQuery.refetch(),
  });

  const removeWishlistMutation = useMutation({
    mutationFn: removeWishlist,
    onSuccess: () => wishlistQuery.refetch(),
  });

  // 🚀 Single Source of Truth for LocalStorage
  const updateLocalStorage = useCallback((newWishlist: WishlistV2) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('wishlist', JSON.stringify(newWishlist));
    }
  }, []);

  // Sync localStorage when wishlist changes
  useEffect(() => {
    updateLocalStorage(wishlist);
  }, [wishlist, updateLocalStorage]);

  // Sync wishlist when fetching from API
  useEffect(() => {
    if (wishlistQuery.data?.wishlist) {
      setWishlist(wishlistQuery.data.wishlist);
    }
  }, [wishlistQuery.data]);

  // 🛒 Add to Wishlist
  const addToWishlist = useCallback(
    (sku: SkuDTO) => {
      setWishlist((prev) => {
        const updatedWishlist: WishlistV2 = {
          ...prev,
          userId: user?.id || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          skus: [...prev.skus, sku],
        };
        if (user?.id) {
          addWishlistMutation.mutate(sku.slug);
        }
        return updatedWishlist;
      });
    },
    [addWishlistMutation, user?.id]
  );

  // ❌ Remove from Wishlist
  const removeFromWishlist = useCallback(
    (slug: string) => {
      setWishlist((prev) => {
        const updatedWishlist: WishlistV2 = {
          ...prev,
          userId: user?.id || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          skus: prev.skus.filter((item) => item.slug !== slug),
        };
        if (user?.id) {
          removeWishlistMutation.mutate(slug);
        }
        return updatedWishlist;
      });
    },
    [removeWishlistMutation, user?.id]
  );

  // 🔄 Toggle Wishlist Item
  const toggleFromWishlist = useCallback(
    (sku: SkuDTO) => {
      if (wishlist.skus.some((item) => item.slug === sku.slug)) {
        removeFromWishlist(sku.slug);
      } else {
        addToWishlist(sku);
      }
    },
    [wishlist.skus, addToWishlist, removeFromWishlist]
  );

  // 🔎 Check if SKU is in Wishlist
  const isInWishlist = useCallback(
    (slug: string) => {
      return slug ? wishlist.skus.some((item) => item.slug === slug) : false;
    },
    [wishlist.skus]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('wishlist', JSON.stringify(wishlist));
    }
  }, [wishlist]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addWishlist: addToWishlist,
        removeWishlist: removeFromWishlist,
        isLoadingWishlist: wishlistQuery.isLoading,
        toggleFromWishlist,
        isInWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContextProvider;
